import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { analyticsAPI } from '../services/api';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const COLORS = ['#16a34a','#2563eb','#dc2626','#f59e0b','#8b5cf6','#06b6d4'];

const ChartCard = ({ title, children, subtitle }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-testid={`chart-${title.toLowerCase().replace(/\s/g,'-')}`}>
    <h4 className="font-semibold text-gray-800 text-sm mb-1">{title}</h4>
    {subtitle && <p className="text-xs text-gray-400 mb-3">{subtitle}</p>}
    <div className="h-56">{children}</div>
  </div>
);

const lineOpts = (yLabel) => ({
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
  scales: { y: { title: { display: true, text: yLabel, font: { size: 11 } }, beginAtZero: false }, x: { ticks: { font: { size: 10 } } } },
  interaction: { intersect: false, mode: 'index' },
});

const barOpts = {
  responsive: true, maintainAspectRatio: false, indexAxis: 'y',
  plugins: { legend: { display: false } },
  scales: { x: { beginAtZero: true } },
};

const TendenciasTab = ({ data }) => {
  if (!data) return <p className="text-gray-400 text-center py-10">Cargando tendencias...</p>;

  const pesoChart = {
    labels: data.peso.map(d => d.mes),
    datasets: [{
      label: 'Peso Promedio (kg)', data: data.peso.map(d => d.promedio),
      borderColor: COLORS[0], backgroundColor: 'rgba(22,163,74,0.08)', fill: true, tension: 0.3,
    }],
  };

  const srvChart = {
    labels: data.servicios.map(d => d.mes),
    datasets: [
      { label: 'Preñadas', data: data.servicios.map(d => d.prenadas), borderColor: COLORS[0], backgroundColor: 'rgba(22,163,74,0.1)', fill: true, tension: 0.3 },
      { label: 'Vacías', data: data.servicios.map(d => d.vacias), borderColor: COLORS[2], backgroundColor: 'rgba(220,38,38,0.08)', fill: true, tension: 0.3 },
    ],
  };

  const tasaPrenezChart = {
    labels: data.servicios.map(d => d.mes),
    datasets: [{
      label: 'Tasa Preñez (%)', data: data.servicios.map(d => d.tasa_prenez),
      borderColor: COLORS[1], backgroundColor: 'rgba(37,99,235,0.08)', fill: true, tension: 0.3,
    }],
  };

  const parChart = {
    labels: data.pariciones.map(d => d.mes),
    datasets: [
      { label: 'EP Promedio (%)', data: data.pariciones.map(d => d.ep_promedio), borderColor: COLORS[4], backgroundColor: 'rgba(139,92,246,0.08)', fill: true, tension: 0.3 },
    ],
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <ChartCard title="Peso Promedio" subtitle="Evolución mensual del peso del rodeo">
        <Line data={pesoChart} options={lineOpts('kg')} />
      </ChartCard>
      <ChartCard title="Servicios Reproductivos" subtitle="Preñadas vs Vacías por mes">
        <Line data={srvChart} options={lineOpts('Cantidad')} />
      </ChartCard>
      <ChartCard title="Tasa de Preñez" subtitle="Porcentaje mensual de preñez">
        <Line data={tasaPrenezChart} options={lineOpts('%')} />
      </ChartCard>
      <ChartCard title="Eficiencia Productiva" subtitle="EP promedio por mes de parición">
        <Line data={parChart} options={lineOpts('EP %')} />
      </ChartCard>
      <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h4 className="font-semibold text-gray-800 text-sm mb-3">Stock Actual</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="rounded-lg bg-green-50 p-4"><p className="text-2xl font-bold text-green-700">{data.stock.total}</p><p className="text-xs text-gray-500">Total Activos</p></div>
          <div className="rounded-lg bg-pink-50 p-4"><p className="text-2xl font-bold text-pink-700">{data.stock.hembras}</p><p className="text-xs text-gray-500">Hembras</p></div>
          <div className="rounded-lg bg-blue-50 p-4"><p className="text-2xl font-bold text-blue-700">{data.stock.machos}</p><p className="text-xs text-gray-500">Machos</p></div>
        </div>
      </div>
    </div>
  );
};

const ComparacionTab = ({ data }) => {
  if (!data) return <p className="text-gray-400 text-center py-10">Cargando comparación...</p>;
  if (data.total_establecimientos < 1) return <p className="text-gray-400 text-center py-10">No hay establecimientos para comparar.</p>;

  const labels = data.comparaciones.map(c => c.nombre);

  const animalChart = {
    labels,
    datasets: [
      { label: 'Hembras', data: data.comparaciones.map(c => c.hembras), backgroundColor: '#ec4899' },
      { label: 'Machos', data: data.comparaciones.map(c => c.machos), backgroundColor: '#3b82f6' },
    ],
  };

  const kpiRows = [
    { label: 'Total Animales', key: 'total_animales', fmt: v => v },
    { label: 'Tasa Preñez', key: 'tasa_prenez', fmt: v => `${v}%` },
    { label: 'EP Promedio', key: 'ep_promedio', fmt: v => `${v}%` },
    { label: 'Peso Promedio', key: 'peso_promedio', fmt: v => `${v} kg` },
    { label: 'Pariciones', key: 'total_pariciones', fmt: v => v },
    { label: 'Alertas Pend.', key: 'alertas_pendientes', fmt: v => v },
  ];

  return (
    <div className="space-y-5">
      {/* KPI comparison table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto" data-testid="comparison-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 font-medium text-gray-600">KPI</th>
              {labels.map((l, i) => (
                <th key={i} className="text-center px-4 py-3 font-medium text-gray-600">{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kpiRows.map((row, ri) => (
              <tr key={ri} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-700">{row.label}</td>
                {data.comparaciones.map((c, ci) => {
                  const vals = data.comparaciones.map(x => x[row.key]);
                  const max = Math.max(...vals);
                  const isMax = c[row.key] === max && max > 0;
                  return (
                    <td key={ci} className={`text-center px-4 py-3 ${isMax ? 'text-green-700 font-bold' : 'text-gray-600'}`}>
                      {row.fmt(c[row.key])}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bar chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Distribución por Sexo" subtitle="Hembras vs Machos por establecimiento">
          <Bar data={animalChart} options={{
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
            scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
          }} />
        </ChartCard>

        <ChartCard title="Tasa de Preñez Comparativa">
          <Bar data={{
            labels,
            datasets: [{ data: data.comparaciones.map(c => c.tasa_prenez), backgroundColor: data.comparaciones.map((_, i) => COLORS[i % COLORS.length]) }],
          }} options={{ ...barOpts, indexAxis: 'x', scales: { y: { beginAtZero: true, max: 100, title: { display: true, text: '%' } } } }} />
        </ChartCard>
      </div>
    </div>
  );
};

const TemporadasTab = ({ data }) => {
  if (!data) return <p className="text-gray-400 text-center py-10">Cargando comparativo...</p>;

  const { año_actual, año_anterior, actual, anterior, deltas, mensual_actual, mensual_anterior } = data;

  const kpiItems = [
    { label: 'Servicios', key: 'servicios', icon: '💉' },
    { label: 'Prenadas', key: 'prenadas', icon: '🤰' },
    { label: 'Tasa Prenez', key: 'tasa_prenez', icon: '📊', suffix: '%' },
    { label: 'Pariciones', key: 'pariciones', icon: '🍼' },
    { label: 'Destetes', key: 'destetes', icon: '🐄' },
    { label: 'EP Promedio', key: 'ep_promedio', icon: '⚡', suffix: '%' },
    { label: 'Peso Promedio', key: 'peso_promedio', icon: '⚖', suffix: ' kg' },
    { label: 'Peso Destete Prom', key: 'peso_destete_promedio', icon: '📏', suffix: ' kg' },
    { label: 'Pesajes Registrados', key: 'pesajes_registrados', icon: '📝' },
    { label: 'Alertas', key: 'alertas', icon: '🔔' },
  ];

  const pesoCompChart = {
    labels: mensual_actual.map(d => d.mes),
    datasets: [
      { label: `${año_actual}`, data: mensual_actual.map(d => d.peso_promedio || null), borderColor: '#16a34a', backgroundColor: 'rgba(22,163,74,0.08)', fill: false, tension: 0.3, pointRadius: 4 },
      { label: `${año_anterior}`, data: mensual_anterior.map(d => d.peso_promedio || null), borderColor: '#9ca3af', backgroundColor: 'rgba(156,163,175,0.08)', fill: false, tension: 0.3, borderDash: [5, 5], pointRadius: 3 },
    ],
  };

  const srvCompChart = {
    labels: mensual_actual.map(d => d.mes),
    datasets: [
      { label: `Servicios ${año_actual}`, data: mensual_actual.map(d => d.servicios), backgroundColor: 'rgba(22,163,74,0.7)', borderRadius: 4 },
      { label: `Servicios ${año_anterior}`, data: mensual_anterior.map(d => d.servicios), backgroundColor: 'rgba(156,163,175,0.5)', borderRadius: 4 },
    ],
  };

  return (
    <div className="space-y-6">
      {/* KPI Comparison Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="temporadas-kpis">
        {kpiItems.map(item => {
          const val_act = actual[item.key] || 0;
          const val_ant = anterior[item.key] || 0;
          const delta = deltas[item.key] || 0;
          const isPositive = delta > 0;
          const isNeutral = delta === 0;
          return (
            <div key={item.key} className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm" data-testid={`temp-kpi-${item.key}`}>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-base">{item.icon}</span>
                <span className="text-xs text-gray-500 truncate">{item.label}</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{val_act}{item.suffix || ''}</p>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-xs text-gray-400">{año_anterior}: {val_ant}{item.suffix || ''}</span>
                {!isNeutral && (
                  <span className={`text-xs font-semibold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                    {isPositive ? '+' : ''}{delta}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Monthly Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title={`Peso Promedio: ${año_actual} vs ${año_anterior}`} subtitle="Evolucion mensual comparada">
          <Line data={pesoCompChart} options={{
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
            scales: {
              y: { title: { display: true, text: 'kg', font: { size: 11 } }, beginAtZero: false },
              x: { ticks: { font: { size: 10 } } },
            },
            interaction: { intersect: false, mode: 'index' },
          }} />
        </ChartCard>
        <ChartCard title={`Servicios: ${año_actual} vs ${año_anterior}`} subtitle="Cantidad mensual por temporada">
          <Bar data={srvCompChart} options={{
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
            scales: { y: { beginAtZero: true, title: { display: true, text: 'Cantidad', font: { size: 11 } } } },
          }} />
        </ChartCard>
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto" data-testid="temporadas-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 font-medium text-gray-600">KPI</th>
              <th className="text-center px-4 py-3 font-medium text-green-700">{año_actual}</th>
              <th className="text-center px-4 py-3 font-medium text-gray-500">{año_anterior}</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Variacion</th>
            </tr>
          </thead>
          <tbody>
            {kpiItems.map((item, i) => {
              const delta = deltas[item.key] || 0;
              return (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-700">{item.icon} {item.label}</td>
                  <td className="text-center px-4 py-3 font-semibold text-gray-900">{actual[item.key] || 0}{item.suffix || ''}</td>
                  <td className="text-center px-4 py-3 text-gray-500">{anterior[item.key] || 0}{item.suffix || ''}</td>
                  <td className={`text-center px-4 py-3 font-semibold ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                    {delta > 0 ? '+' : ''}{delta}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AnalyticsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('tendencias');
  const [tendencias, setTendencias] = useState(null);
  const [comparacion, setComparacion] = useState(null);
  const [temporadas, setTemporadas] = useState(null);
  const [meses, setMeses] = useState(24);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    setLoading(true);
    try {
      const [tRes, cRes, tmpRes] = await Promise.allSettled([
        analyticsAPI.tendencias(user.establecimiento_id, meses),
        analyticsAPI.comparacion(user?.id),
        analyticsAPI.comparativoTemporadas(user.establecimiento_id),
      ]);
      if (tRes.status === 'fulfilled') setTendencias(tRes.value.data);
      if (cRes.status === 'fulfilled') setComparacion(cRes.value.data);
      if (tmpRes.status === 'fulfilled') setTemporadas(tmpRes.value.data);
    } catch { /* */ } finally { setLoading(false); }
  }, [user, meses]);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { id: 'tendencias', label: 'Tendencias' },
    { id: 'temporadas', label: 'Temporadas (Año vs Año)' },
    { id: 'comparacion', label: 'Comparacion Multi-Est.' },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Analytics</h2>
          <p className="text-sm text-gray-500">Tendencias temporales y comparacion entre establecimientos</p>
        </div>
        {activeTab === 'tendencias' && (
          <select value={meses} onChange={e => setMeses(Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm" data-testid="meses-select">
            <option value={6}>6 meses</option>
            <option value={12}>12 meses</option>
            <option value={24}>24 meses</option>
            <option value={36}>36 meses</option>
          </select>
        )}
      </div>

      <div className="flex bg-gray-100 rounded-lg p-1 gap-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${activeTab === t.id ? 'bg-white text-green-700 font-semibold shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`analytics-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {activeTab === 'tendencias' && <TendenciasTab data={tendencias} />}
          {activeTab === 'temporadas' && <TemporadasTab data={temporadas} />}
          {activeTab === 'comparacion' && <ComparacionTab data={comparacion} />}
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
